import { useState, useEffect } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { supabase } from '@/integrations/supabase/client';
import { safeNextPath } from '@/lib/authRedirect';

import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { useToast } from '@/hooks/use-toast';
import { Loader2, CheckCircle, AlertCircle } from 'lucide-react';

type PageState = 'checking' | 'valid' | 'invalid' | 'success';

function friendlyResetErrorMessage(message: string): { kind: 'inline' | 'toast'; text: string } {
  const msg = message.toLowerCase();
  if (msg.includes('different from the old password') || msg.includes('same password')) {
    return { kind: 'inline', text: 'Du kan inte använda samma lösenord igen. Välj ett nytt som skiljer sig från det tidigare.' };
  }
  if (msg.includes('weak password') || msg.includes('password should be')) {
    return { kind: 'inline', text: 'Lösenordet är för svagt. Välj ett lite starkare (minst 8 tecken).' };
  }
  if (msg.includes('too many requests') || msg.includes('rate limit')) {
    return { kind: 'inline', text: 'För många försök. Vänta en stund och prova igen.' };
  }
  if (msg.includes('expired') || msg.includes('invalid') || msg.includes('not authorized')) {
    return { kind: 'inline', text: 'Länken verkar ha gått ut. Begär en ny återställningslänk.' };
  }
  return { kind: 'toast', text: '' };
}

export default function ResetPassword() {
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [pageState, setPageState] = useState<PageState>('checking');
  const [inlineError, setInlineError] = useState<string | null>(null);
  const navigate = useNavigate();
  const { toast } = useToast();

  const rawNext = new URLSearchParams(window.location.search).get('next');
  const nextPath = safeNextPath(rawNext) ?? '/explore';

  // Check if we have a valid recovery session
  useEffect(() => {
    let mounted = true;

    const checkSession = async () => {
      await new Promise(resolve => setTimeout(resolve, 500));
      const { data: { session } } = await supabase.auth.getSession();
      if (!mounted) return;
      if (session) {
        setPageState('valid');
      } else {
        setPageState('invalid');
      }
    };

    const { data: { subscription } } = supabase.auth.onAuthStateChange((event, session) => {
      if (!mounted) return;
      if (event === 'PASSWORD_RECOVERY') {
        setPageState('valid');
      } else if (event === 'SIGNED_IN' && session) {
        setPageState('valid');
      }
    });

    checkSession();

    return () => {
      mounted = false;
      subscription.unsubscribe();
    };
  }, []);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setInlineError(null);

    if (!password || !confirmPassword) {
      setInlineError('Fyll i båda fälten för att fortsätta.');
      return;
    }

    if (password !== confirmPassword) {
      setInlineError('Lösenorden matchar inte. Kontrollera att du skrivit samma i båda fälten.');
      return;
    }

    if (password.length < 8) {
      setInlineError('Lösenordet behöver vara minst 8 tecken.');
      return;
    }

    setIsLoading(true);

    try {
      const { error } = await supabase.auth.updateUser({ password });

      if (error) {
        const result = friendlyResetErrorMessage(error.message);
        if (result.kind === 'inline') {
          setInlineError(result.text);
        } else {
          toast({
            title: 'Något gick inte som det skulle',
            description: 'Försök igen om en stund.',
            variant: 'destructive',
          });
        }
        setIsLoading(false);
        return;
      }

      setPageState('success');
      toast({
        title: 'Lösenord uppdaterat',
        description: 'Du kan nu logga in med ditt nya lösenord.',
      });

      setTimeout(() => {
        navigate(nextPath, { replace: true });
      }, 2000);
    } catch {
      toast({
        title: 'Något gick inte som det skulle',
        description: 'Försök igen om en stund.',
        variant: 'destructive',
      });
      setIsLoading(false);
    }
  };

  if (pageState === 'checking') {
    return (
      <div className="min-h-screen flex items-center justify-center bg-background">
        <Loader2 className="h-8 w-8 animate-spin text-muted-foreground" />
      </div>
    );
  }

  if (pageState === 'invalid') {
    const loginUrl = rawNext ? `/login?next=${encodeURIComponent(rawNext)}` : '/login';

    return (
      <div className="min-h-screen flex items-center justify-center bg-background px-4">
        <Card className="w-full max-w-sm">
          <CardHeader className="text-center">
            <div className="mx-auto mb-4 flex h-12 w-12 items-center justify-center rounded-full bg-destructive/10">
              <AlertCircle className="h-6 w-6 text-destructive" />
            </div>
            <Link to="/" className="mx-auto mb-4">
              <span className="font-display text-2xl font-medium text-foreground">
                Farmly
              </span>
            </Link>
            <CardTitle className="text-xl">Ogiltig länk</CardTitle>
            <CardDescription>
              Återställningslänken är ogiltig eller har gått ut.
            </CardDescription>
          </CardHeader>
          <CardContent className="space-y-4">
            <p className="text-sm text-muted-foreground text-center">
              Öppna länken i e-postmeddelandet för att återställa lösenordet.
              Om länken har gått ut kan du begära en ny.
            </p>
            <Button
              className="w-full"
              onClick={() => navigate(loginUrl)}
            >
              Begär ny länk
            </Button>
          </CardContent>
        </Card>
      </div>
    );
  }

  if (pageState === 'success') {
    return (
      <div className="min-h-screen flex items-center justify-center bg-background px-4">
        <Card className="w-full max-w-sm">
          <CardHeader className="text-center">
            <div className="mx-auto mb-4 flex h-12 w-12 items-center justify-center rounded-full bg-primary/10">
              <CheckCircle className="h-6 w-6 text-primary" />
            </div>
            <CardTitle className="text-xl">Lösenord uppdaterat</CardTitle>
            <CardDescription>
              Ditt lösenord har ändrats.
            </CardDescription>
          </CardHeader>
          <CardContent>
            <Button
              className="w-full"
              onClick={() => navigate(nextPath, { replace: true })}
            >
              Fortsätt
            </Button>
          </CardContent>
        </Card>
      </div>
    );
  }

  return (
    <div className="min-h-screen flex items-center justify-center bg-background px-4">
      <Card className="w-full max-w-sm">
        <CardHeader className="text-center">
          <Link to="/" className="mx-auto mb-4">
            <span className="font-display text-2xl font-medium text-foreground">
              Farmly
            </span>
          </Link>
          <CardTitle className="text-xl">Sätt nytt lösenord</CardTitle>
          <CardDescription>
            Ange ditt nya lösenord nedan.
          </CardDescription>
        </CardHeader>
        <CardContent>
          <form onSubmit={handleSubmit} className="space-y-4">
            <div className="space-y-2">
              <Label htmlFor="new-password">Nytt lösenord</Label>
              <Input
                id="new-password"
                type="password"
                value={password}
                onChange={(e) => { setPassword(e.target.value); setInlineError(null); }}
                placeholder="Minst 8 tecken"
                autoComplete="new-password"
                disabled={isLoading}
              />
              <p className="text-xs text-muted-foreground">
                Minst 8 tecken
              </p>
            </div>
            <div className="space-y-2">
              <Label htmlFor="confirm-password">Bekräfta lösenord</Label>
              <Input
                id="confirm-password"
                type="password"
                value={confirmPassword}
                onChange={(e) => { setConfirmPassword(e.target.value); setInlineError(null); }}
                placeholder="Skriv lösenordet igen"
                autoComplete="new-password"
                disabled={isLoading}
              />
            </div>

            {inlineError && (
              <div className="flex items-start gap-2 rounded-md border border-amber-200 bg-amber-50 p-3 text-sm text-amber-900 dark:border-amber-800 dark:bg-amber-950 dark:text-amber-100">
                <AlertCircle className="h-4 w-4 mt-0.5 shrink-0" />
                <p>{inlineError}</p>
              </div>
            )}

            <Button
              type="submit"
              className="w-full"
              disabled={isLoading || !password || !confirmPassword}
            >
              {isLoading ? (
                <>
                  <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                  Sparar...
                </>
              ) : (
                'Spara nytt lösenord'
              )}
            </Button>
          </form>
        </CardContent>
      </Card>
    </div>
  );
}
